import * as XLSX from "xlsx";
import { Ssq, Heading, Property } from "src/api/sure-api/ssq/useGetOneSsq";
import { Post, PostElement } from "src/api/client-api/post/postSchema.ts";
import { FoodsIcon } from "@foodpilot/foods";
import { Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters.ts";
import { formatUnitAbbreviation } from "src/utils/formatting";

type ExportExcelSSQButtonProps = {
    label: string;
    ssq: Ssq | undefined;
    post: Post | undefined;
};

const exportHeading = (
    heading: Heading,
    parentHeading: Heading | null,
    mappingPropsToValues: { [id: number]: PostElement[] },
    all_rows: string[][],
) => {
    heading.children.forEach((child: Heading) => {
        exportHeading(child, heading, mappingPropsToValues, all_rows);
    });
    heading.properties.forEach((property: Property, i: number) => {
        exportProperty(property, heading, parentHeading, null, i, mappingPropsToValues, all_rows);
    });
};

const formatReponse = (property: Property, element?: PostElement): string => {
    if (!element) return "";
    if (["select", "checkbox", "radio"].includes(property.field?.type ?? "") && property.dataList?.options.length) {
        return (
            element.dataListOptionIds
                ?.map((answer) => property.dataList?.options.find((label) => label.id === answer)?.label)
                .join(", ") ?? ""
        );
    }
    return element.value;
};

const formatReponseGroup = (i: number, element?: PostElement) => {
    return i + 1 + "" + (element?.position !== undefined ? 1 + element.position : "");
};

const exportProperty = (
    property: Property,
    heading: Heading,
    parentHeading: Heading | null,
    parentProperty: Property | null,
    i: number,
    mappingPropsToValues: { [id: number]: PostElement[] },
    all_rows: string[][],
) => {
    if (property.children.length !== 0) {
        property.children.forEach((child: Property) => {
            exportProperty(child, heading, parentHeading, property, i, mappingPropsToValues, all_rows);
        });
    } else if (!property.hidden) {
        const emptyValue = "";
        const rowGenerator = (element?: PostElement) => {
            return [
                parentHeading !== null ? parentHeading.title : heading.title,
                parentHeading !== null ? heading.title : emptyValue,
                parentProperty !== null ? parentProperty.title : emptyValue,
                property.title,
                parentProperty !== null ? formatReponseGroup(i, element) : emptyValue,
                element !== null ? formatReponse(property, element) : emptyValue,
                formatUnitAbbreviation(property.unit),
                property.field?.type ?? "",
                "N",
                "n/a",
            ];
        };

        if (property.id in mappingPropsToValues) {
            mappingPropsToValues[property.id].forEach((e) => {
                all_rows.push(rowGenerator(e));
            });
        } else {
            all_rows.push(rowGenerator());
        }
    }
};

export const exportExcel = (headers: string[], ssq: Ssq, post: Post) => {
    const workbook = XLSX.utils.book_new();
    const all_rows: string[][] = [];
    all_rows.push(headers);
    ssq.headings.forEach((heading) => {
        const mappingPropsToValues: { [id: number]: PostElement[] } = {};
        post.elements?.forEach((element) => {
            if (!(element.ssqPropertyId in mappingPropsToValues)) {
                mappingPropsToValues[element.ssqPropertyId] = [];
            }
            mappingPropsToValues[element.ssqPropertyId].push(element);
        });
        exportHeading(heading, null, mappingPropsToValues, all_rows);
    });
    const worksheet = XLSX.utils.aoa_to_sheet(all_rows);
    const fileName = ("Export " + ssq.title + " " + post.campaign?.label).replace(/[^a-zA-Z_\-0-9 ]/g, "");
    XLSX.utils.book_append_sheet(workbook, worksheet, fileName.substring(0, 31));
    XLSX.writeFile(workbook, fileName + ".xlsx");
};

export const ExportSSQExcelButton = (props: ExportExcelSSQButtonProps) => {
    const { isFeatureActivated } = useApplicationParameters();
    const { t } = useTranslation();
    const { ssq, post, label } = props;

    const excelHeaders = [
        t("ssq.export.excel.theme"),
        t("ssq.export.excel.subtheme"),
        t("ssq.export.excel.parentQuestion"),
        t("ssq.export.excel.question"),
        t("ssq.export.excel.reponseGroup"),
        t("ssq.export.excel.reponse"),
        t("ssq.export.excel.unit"),
        t("ssq.export.excel.type"),
        t("ssq.export.excel.type.document"),
        t("ssq.export.excel.type.comment"),
    ];

    return isFeatureActivated("FS-ssq-export-excel") && ssq !== undefined && post !== undefined ?
            <Box>
                <Box>
                    <Button
                        variant="ternary"
                        LinkComponent={true ? "button" : "a"}
                        disabled={false}
                        href=""
                        target="_blank"
                        endIcon={<FoodsIcon icon="exportDownload" size={3} />}
                        onClick={() => exportExcel(excelHeaders, ssq, post)}
                    >
                        {label}
                    </Button>
                </Box>
            </Box>
        :   <></>;
};
